// SPDX-License-Identifier: AGPL-3.0-or-later

import {parseVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';
import type {RoomSfx} from '@app/features/voice_scene/audio/RoomSfx';
import {
	computeSpeakerAudioParams,
	FLAT_SPEAKER_PARAMS,
	SPEAKER_GAIN,
	type SpatialSpeaker,
	spatialSpeakerKey,
} from '@app/features/voice_scene/audio/SpatialAudioMath';
import type {RoomPoseSource} from '@app/features/voice_scene/hooks/usePositionSender';
import {roomListener} from '@app/features/voice_scene/hooks/useSpatialAudioSync';
import {RoomPositions} from '@app/features/voice_scene/net/PositionBuffer';
import type {Vec3, VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import type {SeatAssignment, SeatParticipant} from '@app/features/voice_scene/utils/SeatAssignment';
import type {RoomPose} from '@fluxer/constants/src/RoomCharacters';
import {
	VOICE_ROOM_SCENE_SOUNDS,
	type VoiceRoomSceneSounds,
	type VoiceRoomSfx,
} from '@fluxer/constants/src/VoiceRoomSounds';
import type {VoiceRoomTheme} from '@fluxer/constants/src/VoiceRoomThemes';
import type {Room} from 'livekit-client';
import {useEffect, useRef} from 'react';

export const WALK_AUDIO_INTERVAL_MS = 200;
// Seconds between footsteps: about 2 a second walking, 3 running.
export const STEP_INTERVAL_S = {walk: 0.5, run: 1 / 3} as const;
// A room without steps of its own (VOICE_ROOM_SCENE_SOUNDS) walks on wood. TEMPORARY: the rug steps in the sound set
// wait for a floor surface in the room file (GLB contract).
const STEP_SOUNDS = [
	'step_wood_1',
	'step_wood_2',
	'step_wood_3',
	'step_wood_4',
] as const satisfies ReadonlyArray<VoiceRoomSfx>;

export interface WalkerSound {
	pose: RoomPose | null;
	nextStepAt: number | null;
	steps: number;
}

export interface WalkSoundEvent {
	identity: string;
	sound: VoiceRoomSfx;
	// Seconds after this tick.
	delayS: number;
	pose: RoomPose;
}

// The steps under a walker: the room's floor, or inside a zone (floor distance to its ses_ node) the zone's.
export function stepSoundsAt(
	steps: VoiceRoomSceneSounds['steps'],
	points: Readonly<Record<string, Vec3>>,
	pose: RoomPose,
): ReadonlyArray<VoiceRoomSfx> {
	if (!steps) return STEP_SOUNDS;
	for (const zone of steps.zones ?? []) {
		const point = points[zone.node];
		if (point && Math.hypot(pose.x - point.x, pose.z - point.z) <= zone.radius) return zone.sounds;
	}
	return steps.sounds;
}

const stepInterval = (pose: RoomPose): number | null =>
	pose.anim === 'walk' ? STEP_INTERVAL_S.walk : pose.anim === 'run' ? STEP_INTERVAL_S.run : null;

// One tick of the room's walking sounds. poses: everyone in the room now (null = in their seat); a walker absent from
// it has left and is forgotten without a sound. Footsteps due before the next tick (windowS) come out with their delay.
export function walkSoundEvents(
	walkers: Map<string, WalkerSound>,
	poses: ReadonlyMap<string, RoomPose | null>,
	nowS: number,
	windowS: number,
	stepSounds: (pose: RoomPose) => ReadonlyArray<VoiceRoomSfx> = () => STEP_SOUNDS,
): Array<WalkSoundEvent> {
	const events: Array<WalkSoundEvent> = [];
	for (const identity of walkers.keys()) if (!poses.has(identity)) walkers.delete(identity);
	for (const [identity, pose] of poses) {
		const previous = walkers.get(identity);
		if (!pose) {
			if (previous?.pose) events.push({identity, sound: 'sit', delayS: 0, pose: previous.pose});
			walkers.set(identity, {pose: null, nextStepAt: null, steps: previous?.steps ?? 0});
			continue;
		}
		if (previous && !previous.pose) events.push({identity, sound: 'stand', delayS: 0, pose});
		const interval = stepInterval(pose);
		let steps = previous?.steps ?? 0;
		let next: number | null = null;
		if (interval !== null) {
			const sounds = stepSounds(pose);
			next = previous?.nextStepAt ?? nowS + interval / 2;
			for (; next < nowS + windowS; next += interval) {
				events.push({
					identity,
					sound: sounds[steps % sounds.length]!,
					delayS: Math.max(0, next - nowS),
					pose,
				});
				steps++;
			}
		}
		walkers.set(identity, {pose, nextStepAt: next, steps});
	}
	return events;
}

// Footsteps, getting up and sitting back down for everyone walking in the room (me too), where they are and quieter
// with distance (the steps at this device's footstep volume), through the room's one event sound player
// (useRoomAmbience's: its deafen, self or moderator, and its one cap on sounds at once). Only while walking is on (walk
// is my pose source, null when it is off) and anyone walks (active): seated in a room where nobody walks, no timer runs.
export function useWalkAudio({
	handle,
	room,
	walk,
	sfx,
	active,
	assignment,
	myUserId,
	theme,
}: {
	handle: VoiceSceneHandle;
	room: Room | null;
	walk: RoomPoseSource | null;
	sfx: RoomSfx | null;
	active: boolean;
	assignment: SeatAssignment<SeatParticipant>;
	myUserId: string | null;
	theme: VoiceRoomTheme | null;
}): void {
	const latest = useRef({handle, assignment, myUserId, theme});
	latest.current = {handle, assignment, myUserId, theme};

	// Kept across the quiet spells: someone walking again later is not a first sighting.
	const walkers = useRef(new Map<string, WalkerSound>()).current;

	useEffect(() => {
		if (!walk || !room || !sfx || !active) return;
		// The seat each walker had when they got up: sitting down on another one is useRoomAmbience's seat change sound.
		const seatAtStand = new Map<string, string | null>();
		const seatOf = (identity: string): string | null =>
			latest.current.assignment.placements.get(parseVoiceParticipantIdentity(identity).userId)?.seat ?? null;

		const tick = (): void => {
			const now = performance.now();
			const local = room.localParticipant.identity;
			const poses = new Map<string, RoomPose | null>([[local, walk.getPose()]]);
			for (const identity of room.remoteParticipants.keys()) poses.set(identity, RoomPositions.pose(identity, now));
			const steps = latest.current.theme ? VOICE_ROOM_SCENE_SOUNDS[latest.current.theme]?.steps : undefined;
			const points = latest.current.handle.layout.soundPoints ?? {};
			const footsteps = VoiceSceneSettings.footstepVolume;
			const events = walkSoundEvents(walkers, poses, now / 1000, WALK_AUDIO_INTERVAL_MS / 1000, (pose) =>
				stepSoundsAt(steps, points, pose),
			).filter((event) => {
				if (event.sound === 'stand') seatAtStand.set(event.identity, seatOf(event.identity));
				if (event.sound !== 'sit') return event.sound === 'stand' || footsteps > 0;
				const seat = seatAtStand.get(event.identity);
				seatAtStand.delete(event.identity);
				return seat === undefined || seat === seatOf(event.identity);
			});
			if (events.length === 0) return;
			const {handle, assignment, myUserId} = latest.current;
			const own = myUserId ? assignment.placements.get(myUserId)?.position : undefined;
			const listener = roomListener(handle, own, poses.get(local) ?? null);
			const speakers: Array<SpatialSpeaker> = events.map((event, index) => ({
				identity: String(index),
				kind: 'mic',
				position: {x: event.pose.x, y: event.pose.y, z: event.pose.z},
				walking: true,
			}));
			const params = VoiceSceneSettings.spatialAudio
				? computeSpeakerAudioParams({listener, speakers}, 'spatial')
				: null;
			events.forEach((event, index) => {
				const at = params?.get(spatialSpeakerKey(String(index), 'mic'));
				sfx.play(event.sound, at?.position ?? FLAT_SPEAKER_PARAMS.position, event.identity, {
					delayS: event.delayS,
					level: (at ? at.gain / SPEAKER_GAIN : 1) * (event.sound === 'stand' || event.sound === 'sit' ? 1 : footsteps),
				});
			});
		};
		const interval = setInterval(tick, WALK_AUDIO_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [walk, room, sfx, active]);
}
